/**
 * SwapExport Component
 *
 * Export swap history for tax reporting purposes.
 * Supports CSV and PDF export formats.
 *
 * Features:
 * - Complete swap chain history
 * - Cost basis tracking through swaps
 * - Gains/losses calculation
 * - French tax form (2086) compatible format
 * - Date range filtering
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import { fmt, iso } from '../../utils/formatters';
import { getSwapChain, calculateOriginalCostBasis } from '../../utils/costBasisTracking';
import type { Token, Position, Prices } from '../../types';

interface SwapExportProps {
  tokens: Token[];
  prices: Prices;
  isOpen: boolean;
  onClose: () => void;
}

interface SwapExportRow {
  date: string;
  fromToken: string;
  fromQuantity: number;
  fromValueUsd: number;
  toToken: string;
  toQuantity: number;
  toValueUsd: number;
  originalCostBasis: number;
  gainLoss: number;
  gainLossPercent: number;
  fees: number;
  platform: string;
  sourceChain: string;
  destChain: string;
  bridgeProtocol: string;
  txHash: string;
  swapChainId: string;
}

export function SwapExport({ tokens, prices, isOpen, onClose }: SwapExportProps) {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [includeChainDetails, setIncludeChainDetails] = useState(true);

  // Collect all swap positions and build export data
  const swapData = useMemo<SwapExportRow[]>(() => {
    const rows: SwapExportRow[] = [];

    tokens.forEach(token => {
      token.positions
        .filter(p => p.is_swap === true)
        .forEach(position => {
          // Find original token in swap chain
          let fromToken = 'Unknown';
          let fromQuantity = 0;
          let fromValueUsd = 0;

          if (position.swap_chain_id) {
            const chain = getSwapChain(position, tokens);
            const currentIndex = chain.findIndex(p => p.id === position.id);
            if (currentIndex > 0) {
              const prevPosition = chain[currentIndex - 1];
              const prevToken = tokens.find(t =>
                t.positions.some(p => p.id === prevPosition.id)
              );
              if (prevToken) {
                fromToken = prevToken.displayName || prevToken.id;
                fromQuantity = prevPosition.quantity;
                fromValueUsd = prevPosition.quantity * prevPosition.price;
              }
            }
          }

          const currentPrice = prices[token.id]?.usd || position.price;
          const toValueUsd = position.quantity * position.price;
          const currentValue = position.quantity * currentPrice;
          const originalCost = position.original_cost_basis
            ? position.quantity * position.original_cost_basis
            : toValueUsd;
          const gainLoss = currentValue - originalCost;
          const gainLossPercent = originalCost > 0
            ? ((currentValue - originalCost) / originalCost) * 100
            : 0;
          const fees = (position.bridge_fee || 0) + (position.gas_fee_usd || 0);

          rows.push({
            date: position.date,
            fromToken,
            fromQuantity,
            fromValueUsd,
            toToken: token.displayName || token.id,
            toQuantity: position.quantity,
            toValueUsd,
            originalCostBasis: originalCost,
            gainLoss,
            gainLossPercent,
            fees,
            platform: position.swap_platform || '',
            sourceChain: position.source_chain || '',
            destChain: position.destination_chain || '',
            bridgeProtocol: position.bridge_protocol || '',
            txHash: position.bridge_tx_hash || '',
            swapChainId: position.swap_chain_id || '',
          });
        });
    });

    // Sort by date
    rows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Apply date filter
    return rows.filter(row => {
      if (dateFrom && row.date < dateFrom) return false;
      if (dateTo && row.date > dateTo) return false;
      return true;
    });
  }, [tokens, prices, dateFrom, dateTo]);

  // Calculate totals
  const totals = useMemo(() => {
    return swapData.reduce((acc, row) => ({
      totalVolume: acc.totalVolume + row.toValueUsd,
      totalGainLoss: acc.totalGainLoss + row.gainLoss,
      totalFees: acc.totalFees + row.fees,
    }), { totalVolume: 0, totalGainLoss: 0, totalFees: 0 });
  }, [swapData]);

  // Export to CSV
  const exportCSV = () => {
    const headers = [
      'Date',
      'Token Source',
      'Quantité Source',
      'Valeur Source (USD)',
      'Token Destination',
      'Quantité Destination',
      'Valeur Destination (USD)',
      'Coût Origine (USD)',
      'Plus/Moins Value (USD)',
      'Plus/Moins Value (%)',
      'Frais (USD)',
    ];

    if (includeChainDetails) {
      headers.push(
        'Plateforme',
        'Chaîne Source',
        'Chaîne Destination',
        'Bridge',
        'TX Hash',
        'Swap Chain ID'
      );
    }

    const csvRows = [headers.join(';')];

    swapData.forEach(row => {
      const values = [
        row.date,
        row.fromToken,
        fmt(row.fromQuantity, 8),
        fmt(row.fromValueUsd, 2),
        row.toToken,
        fmt(row.toQuantity, 8),
        fmt(row.toValueUsd, 2),
        fmt(row.originalCostBasis, 2),
        fmt(row.gainLoss, 2),
        fmt(row.gainLossPercent, 2),
        fmt(row.fees, 2),
      ];

      if (includeChainDetails) {
        values.push(
          row.platform,
          row.sourceChain,
          row.destChain,
          row.bridgeProtocol,
          row.txHash,
          row.swapChainId
        );
      }

      csvRows.push(values.join(';'));
    });

    // Add totals row
    csvRows.push('');
    csvRows.push(`TOTAL;;; ${fmt(totals.totalVolume, 2)};;;; ${fmt(totals.totalGainLoss, 2)}; ${fmt(totals.totalFees, 2)}`);

    const csvContent = csvRows.join('\n');
    downloadFile(csvContent, `swaps_export_${iso(new Date())}.csv`, 'text/csv');
  };

  // Export to JSON
  const exportJSON = () => {
    const exportData = {
      exportDate: iso(new Date()),
      dateRange: { from: dateFrom || 'all', to: dateTo || 'all' },
      totals: {
        totalSwaps: swapData.length,
        totalVolumeUsd: totals.totalVolume,
        totalGainLossUsd: totals.totalGainLoss,
        totalFeesUsd: totals.totalFees,
      },
      swaps: swapData,
    };

    const jsonContent = JSON.stringify(exportData, null, 2);
    downloadFile(jsonContent, `swaps_export_${iso(new Date())}.json`, 'application/json');
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    if (exportFormat === 'csv') {
      exportCSV();
    } else {
      exportJSON();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay show"
      onClick={(e) => (e.target as HTMLElement).className === 'modal-overlay show' && onClose()}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icons.Download size={20} />
            {t('swapExport.title', 'Export des Swaps')}
          </div>
        </div>

        {/* Summary */}
        <div style={{
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>
            {t('swapExport.summary', 'Résumé')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {t('swapExport.totalSwaps', 'Swaps')}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>{swapData.length}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {t('swapExport.totalVolume', 'Volume')}
              </div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>${fmt(totals.totalVolume, 0)}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                {t('swapExport.gainLoss', '+/- Value')}
              </div>
              <div style={{
                fontSize: '18px',
                fontWeight: 600,
                color: totals.totalGainLoss >= 0 ? '#22c55e' : '#ef4444',
              }}>
                {totals.totalGainLoss >= 0 ? '+' : ''}${fmt(totals.totalGainLoss, 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Date Range Filter */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
            {t('swapExport.dateRange', 'Période')}
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '11px' }}>{t('swapExport.from', 'Du')}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '11px' }}>{t('swapExport.to', 'Au')}</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Export Format */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
            {t('swapExport.format', 'Format')}
          </label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <label style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: exportFormat === 'csv' ? '2px solid var(--accent)' : '1px solid var(--border)',
              background: exportFormat === 'csv' ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg)',
              cursor: 'pointer',
              textAlign: 'center',
            }}>
              {/* `.sr-only` instead of `hidden` so keyboard users can focus
                  the radio + screen readers see the role. The visual
                  selection state is carried by the parent <label>'s
                  border + bg, which the user already sees changing. */}
              <input
                type="radio"
                name="format"
                value="csv"
                checked={exportFormat === 'csv'}
                onChange={() => setExportFormat('csv')}
                className="sr-only"
              />
              <Icons.FileText size={20} style={{ marginBottom: '4px' }} />
              <div style={{ fontSize: '13px', fontWeight: 600 }}>CSV</div>
              <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Excel compatible</div>
            </label>
            <label style={{
              flex: 1,
              padding: '12px',
              borderRadius: '8px',
              border: exportFormat === 'json' ? '2px solid var(--accent)' : '1px solid var(--border)',
              background: exportFormat === 'json' ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg)',
              cursor: 'pointer',
              textAlign: 'center',
            }}>
              <input
                type="radio"
                name="format"
                value="json"
                checked={exportFormat === 'json'}
                onChange={() => setExportFormat('json')}
                className="sr-only"
              />
              <Icons.Code size={20} style={{ marginBottom: '4px' }} />
              <div style={{ fontSize: '13px', fontWeight: 600 }}>JSON</div>
              <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Données complètes</div>
            </label>
          </div>
        </div>

        {/* Options */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            fontSize: '13px',
          }}>
            <input
              type="checkbox"
              checked={includeChainDetails}
              onChange={(e) => setIncludeChainDetails(e.target.checked)}
              style={{ width: '16px', height: '16px' }}
            />
            {t('swapExport.includeChainDetails', 'Inclure les détails cross-chain (plateforme, bridge, tx hash)')}
          </label>
        </div>

        {/* Tax Info */}
        <div style={{
          padding: '12px',
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '8px',
          marginBottom: '16px',
          fontSize: '12px',
          color: '#f59e0b',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
        }}>
          <Icons.AlertTriangle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>{t('swapExport.taxNote', 'Note fiscale:')}</strong>{' '}
            {t('swapExport.taxNoteText', 'En France, chaque swap crypto-à-crypto peut constituer un fait générateur d\'imposition. Consultez un professionnel pour votre déclaration.')}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </button>
          <button
            onClick={handleExport}
            disabled={swapData.length === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: swapData.length === 0 ? 0.5 : 1,
            }}
          >
            <Icons.Download size={16} />
            {t('swapExport.export', 'Exporter')} ({swapData.length} swaps)
          </button>
        </div>
      </div>
    </div>
  );
}
